import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Search, MoreHorizontal, Eye, RotateCcw, UserPlus, Ban, History,
  ChevronLeft, ChevronRight, ArrowUp, ArrowDown,
} from "lucide-react";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "../ui/dropdown-menu";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { useLanguage } from "../../context/LanguageContext";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { formatCaseNumber } from "../../lib/caseFormat";
import CaseStatusPill from "../case/CaseStatusPill";

const STATUS_OPTIONS = [
  "submitted", "under_review",
  "approved", "paid",
  "pending_re_edit",
  "rejected", "closed", "draft",
];

const SORTABLE_FIELDS = new Set(["submitted_at", "updated_at", "created_at", "status"]);

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const StatTile = ({ label, value, accent, testId }) => (
  <div
    className="bg-white border border-slate-200 rounded-lg shadow-sm p-4"
    data-testid={testId}
  >
    <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
    <p className={`font-display text-3xl tracking-tight font-medium mt-1 ${accent || "text-[#0A2540]"}`}>
      {value}
    </p>
  </div>
);

const SortHeader = ({ field, label, sortField, sortDir, onSort, testId }) => {
  const active = sortField === field;
  const Arrow = sortDir === "asc" ? ArrowUp : ArrowDown;
  return (
    <th className="px-6 py-3 text-left">
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors focus-ring rounded ${
          active ? "text-[#0A2540]" : "text-slate-500 hover:text-[#0A2540]"
        }`}
        data-testid={testId}
      >
        {label}
        {active ? <Arrow className="h-3 w-3" /> : null}
      </button>
    </th>
  );
};

const ReassignDialog = ({ open, onOpenChange, caseId, onDone, currentReviewerId }) => {
  const { t } = useLanguage();
  const [reviewers, setReviewers] = useState([]);
  const [selected, setSelected] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected("");
    (async () => {
      try {
        const { data } = await api.get("/admin/users", {
          params: { role: "reviewer", status: "active" },
        });
        setReviewers(data.filter((r) => r.id !== currentReviewerId));
      } catch {
        toast.error(t("err_generic"));
      }
    })();
  }, [open, currentReviewerId, t]);

  const onSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await api.post(`/admin/cases/${caseId}/reassign`, { reviewer_id: selected });
      toast.success(t("toast_reassigned"));
      onDone && onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>{t("reassign_dialog_title")}</DialogTitle>
          <DialogDescription>{t("reassign_dialog_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reassign-reviewer">{t("reassign_select_label")}</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger id="reassign-reviewer" data-testid="reassign-reviewer-select">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              {reviewers.map((r) => (
                <SelectItem key={r.id} value={r.id}>
                  {r.name} · {r.email}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("modal_cancel")}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!selected || submitting}
            className="bg-[#0A2540] hover:bg-[#051626] text-white"
            data-testid="reassign-submit-button"
          >
            {t("reassign_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmForceReleaseDialog = ({ open, onOpenChange, caseId, onDone }) => {
  const { t } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const onConfirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/admin/cases/${caseId}/force-release`);
      toast.success(t("toast_force_released"));
      onDone && onDone();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="force-release-dialog">
        <DialogHeader>
          <DialogTitle>{t("force_release_dialog_title")}</DialogTitle>
          <DialogDescription>{t("force_release_dialog_desc")}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("modal_cancel")}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="force-release-submit-button"
          >
            {t("force_release_confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdminCasesTab = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // initialise from URL query string so reload preserves view
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(() => Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1));
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [reviewers, setReviewers] = useState([]);
  const [filters, setFilters] = useState(() => ({
    status: searchParams.get("status") || "all",
    agent_id: searchParams.get("agent_id") || "all",
    reviewer_id: searchParams.get("reviewer_id") || "all",
    q: searchParams.get("q") || "",
  }));
  const [sortField, setSortField] = useState(() => {
    const s = searchParams.get("sort") || "updated_at:desc";
    const [f] = s.split(":");
    return SORTABLE_FIELDS.has(f) ? f : "updated_at";
  });
  const [sortDir, setSortDir] = useState(() => {
    const s = searchParams.get("sort") || "updated_at:desc";
    const [, d] = s.split(":");
    return d === "asc" ? "asc" : "desc";
  });
  const [forceReleaseId, setForceReleaseId] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [returnFor, setReturnFor] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [returnBusy, setReturnBusy] = useState(false);


  // sync state -> URL whenever any of these change
  useEffect(() => {
    const next = new URLSearchParams();
    if (filters.status !== "all") next.set("status", filters.status);
    if (filters.agent_id !== "all") next.set("agent_id", filters.agent_id);
    if (filters.reviewer_id !== "all") next.set("reviewer_id", filters.reviewer_id);
    if (filters.q.trim()) next.set("q", filters.q.trim());
    if (page !== 1) next.set("page", String(page));
    next.set("sort", `${sortField}:${sortDir}`);
    setSearchParams(next, { replace: true });
  }, [filters, page, sortField, sortDir, setSearchParams]);

  const loadCases = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize, sort: `${sortField}:${sortDir}` };
      if (filters.status !== "all") params.status = filters.status;
      if (filters.agent_id !== "all") params.agent_id = filters.agent_id;
      if (filters.reviewer_id !== "all") params.reviewer_id = filters.reviewer_id;
      if (filters.q.trim()) params.q = filters.q.trim();
      const { data } = await api.get("/admin/cases", { params });
      setItems(data.items);
      setTotal(data.total);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, sortField, sortDir, t]);

  useEffect(() => {
    loadCases();
  }, [loadCases]);

  // Load agent + reviewer dropdowns once
  useEffect(() => {
    (async () => {
      try {
        const [a, r] = await Promise.all([
          api.get("/admin/users", { params: { role: "agent" } }),
          api.get("/admin/users", { params: { role: "reviewer" } }),
        ]);
        setAgents(a.data);
        setReviewers(r.data);
      } catch {
        /* ignore */
      }
    })();
  }, []);

  const stats = useMemo(() => {
    const c = { total, pool: 0, under_review: 0, approved: 0, paid: 0,
                pending_re_edit: 0, rejected: 0, closed: 0 };
    for (const it of items) {
      if ((it.status === "submitted" || it.status === "resubmitted") && !it.reviewer_id) c.pool += 1;
      if (it.status === "under_review") c.under_review += 1;
      if (it.status === "approved") c.approved += 1;
      if (it.status === "paid") c.paid += 1;
      if (it.status === "pending_re_edit") c.pending_re_edit += 1;
      if (it.status === "rejected") c.rejected += 1;
      if (it.status === "closed") c.closed += 1;
    }
    return c;
  }, [items, total]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const setFilter = (k, v) => setFilters((s) => ({ ...s, [k]: v }));
  const onClose = async (id) => {
    try {
      await api.post(`/cases/${id}/close`);
      toast.success(t("toast_closed"));
      loadCases();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("err_generic"));
    }
  };

  const onSort = (field) => {
    if (!SORTABLE_FIELDS.has(field)) return;
    if (field === sortField) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  return (
    <div className="space-y-6" data-testid="admin-cases-tab">
      <div className="grid grid-cols-2 md:grid-cols-8 gap-3">
        <StatTile label={t("admin_cases_stat_total")} value={total} testId="admin-cases-stat-total" />
        <StatTile label={t("admin_cases_stat_pool")} value={stats.pool} accent="text-blue-700" testId="admin-cases-stat-pool" />
        <StatTile label={t("admin_cases_stat_review")} value={stats.under_review} accent="text-amber-700" testId="admin-cases-stat-review" />
        <StatTile label={t("admin_cases_stat_approved")} value={stats.approved} accent="text-emerald-700" testId="admin-cases-stat-approved" />
        <StatTile label={t("admin_cases_stat_paid")} value={stats.paid} accent="text-teal-700" testId="admin-cases-stat-paid" />
        <StatTile label={t("admin_cases_stat_pending_re_edit")} value={stats.pending_re_edit} accent="text-orange-600" testId="admin-cases-stat-pending-re-edit" />
        <StatTile label={t("admin_cases_stat_rejected")} value={stats.rejected} accent="text-rose-700" testId="admin-cases-stat-rejected" />
        <StatTile label={t("admin_cases_stat_closed")} value={stats.closed} accent="text-slate-500" testId="admin-cases-stat-closed" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm">
        <div className="px-4 sm:px-6 py-4 border-b border-slate-200 grid gap-3 md:grid-cols-[1fr_auto_auto_auto]">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              value={filters.q}
              onChange={(e) => { setPage(1); setFilter("q", e.target.value); }}
              placeholder={t("admin_cases_search")}
              className="pl-9"
              data-testid="admin-cases-search"
            />
          </div>
          <Select value={filters.status} onValueChange={(v) => { setPage(1); setFilter("status", v); }}>
            <SelectTrigger className="min-w-[150px]" data-testid="admin-cases-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin_filter_all")} · {t("admin_cases_filter_status")}</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s}>{t(`case_status_${s}`)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.agent_id} onValueChange={(v) => { setPage(1); setFilter("agent_id", v); }}>
            <SelectTrigger className="min-w-[160px]" data-testid="admin-cases-filter-agent">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin_filter_all")} · {t("admin_cases_filter_agent")}</SelectItem>
              {agents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filters.reviewer_id} onValueChange={(v) => { setPage(1); setFilter("reviewer_id", v); }}>
            <SelectTrigger className="min-w-[180px]" data-testid="admin-cases-filter-reviewer">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("admin_filter_all")} · {t("admin_cases_filter_reviewer")}</SelectItem>
              <SelectItem value="null" data-testid="admin-cases-filter-reviewer-unassigned">
                {t("admin_cases_filter_unassigned")}
              </SelectItem>
              {reviewers.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-cases-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider w-24">
                  {t("case_number_label")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {t("admin_cases_col_applicant")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {t("admin_cases_col_agent")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {t("admin_cases_col_reviewer")}
                </th>
                <SortHeader
                  field="status" label={t("admin_cases_col_status")}
                  sortField={sortField} sortDir={sortDir} onSort={onSort}
                  testId="admin-cases-sort-status"
                />
                <SortHeader
                  field="submitted_at" label={t("admin_cases_col_submitted")}
                  sortField={sortField} sortDir={sortDir} onSort={onSort}
                  testId="admin-cases-sort-submitted"
                />
                <SortHeader
                  field="created_at" label={t("admin_cases_col_created")}
                  sortField={sortField} sortDir={sortDir} onSort={onSort}
                  testId="admin-cases-sort-created"
                />
                <SortHeader
                  field="updated_at" label={t("admin_cases_col_updated")}
                  sortField={sortField} sortDir={sortDir} onSort={onSort}
                  testId="admin-cases-sort-updated"
                />
                <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  {t("admin_col_actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-6 py-12 text-center">
                  <div className="inline-block h-6 w-6 border-2 border-slate-300 border-t-[#0A2540] rounded-full animate-spin" />
                </td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={9} className="px-10 py-16 text-center text-slate-500" data-testid="admin-cases-empty">
                  {t("admin_cases_no_match")}
                </td></tr>
              ) : (
                items.map((c) => {
                  const canForceRelease = c.reviewer_id && (c.status === "under_review" || c.status === "resubmitted");
                  const canReassign = c.reviewer_id && (c.status === "under_review" || c.status === "resubmitted");
                  const canClose = c.status === "paid";
                  // Phase 15: admin can Return-for-Correction from submitted or under_review.
                  const canReturn = c.status === "submitted" || c.status === "under_review";
                  return (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`admin-case-row-${c.id}`}>
                      <td className="px-6 py-4 align-middle text-sm font-mono text-[#0A2540]" data-testid={`admin-case-number-${c.id}`}>
                        {formatCaseNumber(c.case_number)}
                      </td>
                      <td className="px-6 py-4 align-middle text-sm font-medium text-[#0A2540]">
                        {c.applicant_name || <span className="italic text-slate-400">{t("case_form_new_title")}</span>}
                      </td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-600">{c.agent_name || "—"}</td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-600">
                        {c.reviewer_name || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-6 py-4 align-middle"><CaseStatusPill status={c.status} /></td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-500">{formatDate(c.submitted_at)}</td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-500">{formatDate(c.created_at)}</td>
                      <td className="px-6 py-4 align-middle text-sm text-slate-500">{formatDate(c.updated_at)}</td>
                      <td className="px-6 py-4 align-middle text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/cases/${c.id}`)}
                            className="text-[#0A2540] hover:text-[#007AFF]"
                            data-testid={`admin-case-open-${c.id}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            {t("admin_cases_action_open")}
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-slate-500 hover:text-[#0A2540]"
                                data-testid={`admin-case-menu-${c.id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                disabled={!canForceRelease}
                                onClick={() => setForceReleaseId(c.id)}
                                data-testid={`admin-case-force-release-${c.id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-2" />
                                {t("admin_cases_action_force_release")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canReassign}
                                onClick={() => setReassignFor(c)}
                                data-testid={`admin-case-reassign-${c.id}`}
                              >
                                <UserPlus className="h-4 w-4 mr-2" />
                                {t("admin_cases_action_reassign")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canReturn}
                                onClick={() => setReturnFor(c)}
                                data-testid={`admin-case-return-${c.id}`}
                              >
                                <History className="h-4 w-4 mr-2" />
                                {t("return_for_correction")}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                disabled={!canClose}
                                onClick={() => onClose(c.id)}
                                data-testid={`admin-case-close-${c.id}`}
                              >
                                <Ban className="h-4 w-4 mr-2" />
                                {t("admin_cases_action_close")}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 ? (
          <div className="px-4 sm:px-6 py-3 border-t border-slate-200 flex items-center justify-between text-sm">
            <p className="text-slate-500">
              Page {page} of {totalPages} · {total} cases
            </p>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} data-testid="admin-cases-prev">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} data-testid="admin-cases-next">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      <ConfirmForceReleaseDialog
        open={!!forceReleaseId}
        onOpenChange={(v) => !v && setForceReleaseId(null)}
        caseId={forceReleaseId}
        onDone={loadCases}
      />
      <ReassignDialog
        open={!!reassignFor}
        onOpenChange={(v) => !v && setReassignFor(null)}
        caseId={reassignFor?.id}
        currentReviewerId={reassignFor?.reviewer_id}
        onDone={loadCases}
      />
      {/* Phase 15 — Return for Correction dialog */}
      <Dialog open={!!returnFor} onOpenChange={(v) => { if (!v) { setReturnFor(null); setReturnReason(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="admin-return-dialog">
          <DialogHeader>
            <DialogTitle>{t("return_for_correction_title")}</DialogTitle>
            <DialogDescription>{t("return_for_correction_helper")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="admin-return-reason">{t("resubmit_for_edit_reason")} *</Label>
            <Textarea
              id="admin-return-reason"
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder={t("return_for_correction_reason_placeholder")}
              data-testid="admin-return-reason-input"
            />
            <p className="text-xs text-slate-500">{returnReason.trim().length}/5</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReturnFor(null); setReturnReason(""); }} disabled={returnBusy}>
              {t("modal_cancel")}
            </Button>
            <Button
              onClick={async () => {
                if (!returnFor || returnReason.trim().length < 5) return;
                setReturnBusy(true);
                try {
                  await api.post(`/cases/${returnFor.id}/resubmit-for-edit`,
                                 { reason: returnReason.trim() });
                  toast.success(t("toast_resubmitted_for_edit"));
                  setReturnFor(null); setReturnReason("");
                  loadCases();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || t("err_generic"));
                } finally {
                  setReturnBusy(false);
                }
              }}
              disabled={returnBusy || returnReason.trim().length < 5}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="admin-return-submit-button"
            >
              <History className="h-4 w-4 mr-2" />
              {t("return_for_correction")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminCasesTab;
